/**
 * ProfilePage — single, role-aware profile page for every user type.
 *
 * The page is intentionally thin: it hydrates the full profile (the cached
 * `cs_user` object is trimmed, so we re-fetch via refreshUser to get gender,
 * DOB, KYC, lastLogin, consent, etc.), renders the shared header, then a pill
 * tab bar that groups the role-specific section registry into Account /
 * Notifications / Security / Privacy. The active tab is synced to `?tab=` so
 * the view is deep-linkable and back/forward friendly (matching the Admin
 * Dashboard's tab convention).
 */

import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../context/AuthContext';
import ProfileHeaderCard from '../components/profile/ProfileHeaderCard';
import { getProfileTabs } from '../components/profile/sections';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // The stored user is a trimmed "safe" object; pull the full record once on
  // mount so role/identity/consent fields are all present.
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const tabs = useMemo(() => getProfileTabs(user?.roleName), [user?.roleName]);

  if (!user) {
    return (
      <AppLayout pageTitle="My Profile">
        <div className="text-[13px] text-gray-500">Loading your profile…</div>
      </AppLayout>
    );
  }

  // Active tab from the URL, falling back to the first tab for a missing or
  // unknown `?tab=` value.
  const requested = searchParams.get('tab');
  const activeId = tabs.some((t) => t.id === requested) ? requested : tabs[0].id;
  const activeTab = tabs.find((t) => t.id === activeId) || tabs[0];

  const changeTab = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    // replace (not push) so flipping tabs doesn't stack history entries.
    setSearchParams(next, { replace: true });
  };

  return (
    <AppLayout pageTitle="My Profile">
      <div className="text-[26px] font-bold text-gray-900 mb-[6px]" style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '.2px' }}>
        My Profile
      </div>
      {/* Thin gold accent line under the page title — mirrors the hero hairline */}
      <div
        className="h-[2px] w-[64px] mb-3"
        style={{ background: 'linear-gradient(90deg,#b8973a 0%,transparent 100%)', borderRadius: 2 }}
      />
      <div className="text-[13px] text-gray-500 mb-6">
        Manage your identity, security, and privacy preferences
      </div>

      <ProfileHeaderCard user={user} />

      {/* Tab navigation — pill style, mirrors AdminDashboardPage */}
      <div className="mb-5">
        <div
          className="flex flex-wrap gap-[3px] p-[3px] rounded-[10px] border"
          style={{ borderColor: '#ddd6c6', background: '#f0ece4' }}
          role="tablist"
          aria-label="Profile sections"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.id === activeId;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => changeTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-[7px] rounded-[8px] text-[12px] font-semibold transition-all duration-200 ${
                  isActive
                    ? ''
                    : 'text-[#7a7060] hover:text-[#08111f] hover:bg-white/60'
                }`}
                style={isActive ? { background: '#fff', color: 'var(--teal)', boxShadow: '0 1px 4px rgba(8,17,31,0.10)' } : {}}
              >
                <Icon className="text-[14px]" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active tab content */}
      <div className="space-y-6">
        {activeTab.sections.map(({ key, Component }) => (
          <Component key={key} user={user} />
        ))}
      </div>
    </AppLayout>
  );
}
